import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { red } from '~theme';
import { config } from '../../configuration';
import { addToast } from '~features/toast/ducks';

import uploadSVG from '~images/upload_cloud.svg';
import crossSVG from '~images/red_cross.svg';

import File from './File';

const axios = require('axios');

const blue = '#427aaf';

const getInstructionText = (active, reject) => {
  if (active && reject) {
    return 'Tiedostomuotoa ei voi lisätä';
  }

  if (active) {
    return 'Pudota tiedosto tähän';
  }

  return 'Raahaa ja pudota tiedostot tähän';
};

const getName = upload => {
  if (upload.file) return upload.file.name;
  if (upload.originalFilename) return upload.originalFilename;
  return 'Tuntematon tiedostonimi';
};

// const filterIDs = uploads =>
//   uploads
//     .filter(u => u.id)
//     .map(u => ({
//       id: u.id,
//     }));

const Dropzone = props => {
  const { accept, width } = props;
  // Do this to omit the prop completely if not specified
  const acceptProps = accept ? { accept } : {};
  const [, updateState] = useState();
  const forceUpdate = useCallback(() => updateState({}), []);
  const dispatch = useDispatch();
  // Uploads in progress
  const [uploads, setUploads] = useState([]);
  const [erroredUploads, setErroredUploads] = useState([]);
  // value: completed or old uploads
  const { onChange, value } = props.input ? props.input : props;
  const { noHeader, endpoint, deleteOnRemove, placeholder } = props;

  // Some refs to use inside callbacks for up-to-dateness
  const valueRef = useRef(value);
  valueRef.current = value;
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;
  const erroredUploadsRef = useRef(erroredUploads);
  erroredUploadsRef.current = erroredUploads;

  const onDrop = useCallback(
    async acceptedFiles => {
      // Add files to local state
      // Filter out duplicate names
      const localUploads = acceptedFiles
        .map(f => ({
          file: f,
          progress: null,
          id: null,
        }))
        .filter(
          // Make sure the file wasn't already uploaded as well (valueRef)
          u =>
            ![...(uploadsRef.current || []), ...(valueRef.current || [])].find(
              f => getName(f) === getName(u)
            )
        );

      const newUploads = [...uploadsRef.current, ...localUploads];

      setUploads(newUploads);

      localUploads.forEach(async upload => {
        const formData = new FormData();
        formData.append('file', upload.file);

        try {
          const response = await axios.request({
            method: 'post',
            url: `${config.apiUrl}${endpoint || 'attachments'}`,
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: p => {
              // This might not exactly be the preferred way to do this as we are
              // manipulating an object in the local state directly
              // However, updating the state properly here just makes everything
              // a broken mess
              // Update: Could use refs, but why bother?
              // eslint-disable-next-line no-param-reassign
              upload.progress = p;
              forceUpdate();
            },
          });

          // eslint-disable-next-line no-param-reassign
          upload.id = response.data.id;
          // Upload is finished, so move it over to field value
          // Do use refs here because otherwise your data might be stale thanks to
          // parallel uploads
          setUploads(uploadsRef.current.filter(u => u.file !== upload.file));
          onChange([...(valueRef.current || []), upload]);
          forceUpdate();
          dispatch(
            addToast({
              message: `Tiedosto ${upload.file.name} liitetty`,
              type: 'SUCCESS',
            })
          );
        } catch (e) {
          dispatch(
            addToast({
              message: 'Virhe liitetiedostoa ladatessa',
              type: 'ERROR',
            })
          );
          console.error(`Error in Dropzone: ${e}`);
          setUploads(uploadsRef.current.filter(u => u.file !== upload.file));
          setErroredUploads([...erroredUploadsRef.current, upload]);
        }
      });
    },
    [uploads]
  );

  const handleRemove = async upload => {
    // If it's in progress
    const filteredUploads = uploads.filter(u => upload !== u);
    setUploads(filteredUploads);

    // If we want to instantly nuke the attachment (project page)
    if (deleteOnRemove) {
      try {
        await axios.request({
          method: 'delete',
          url: `${config.apiUrl}${endpoint || 'attachments'}/${upload.id}`,
        });
        dispatch(
          addToast({
            message: `Tiedosto ${getName(upload)} poistettu`,
            type: 'SUCCESS',
          })
        );
      } catch (e) {
        dispatch(
          addToast({
            message: 'Virhe liitetiedostoa poistaessa',
            type: 'ERROR',
          })
        );
        console.error('Error in Dropzone when deleting an attachment:', e);
      }
    }

    // If it's finished
    const filteredValue = value.filter(u => upload.file !== u.file);
    onChange(filteredValue.length ? filteredValue : null); // prefer null over []

    // If it's errored
    const filteredErroredUploads = erroredUploads.filter(u => upload !== u);
    setErroredUploads(filteredErroredUploads);
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({ onDrop, ...acceptProps });

  return (
    <div>
      {!noHeader && <Header>{placeholder || 'Lataa liitteitä'}</Header>}
      <DropArea
        {...getRootProps()}
        active={isDragActive}
        reject={isDragReject}
        width={width}
      >
        <input {...getInputProps()} />
        <Instructions>
          <UploadIcon src={isDragReject ? crossSVG : uploadSVG} />
          <InstructionText>
            {getInstructionText(isDragActive, isDragReject)}
          </InstructionText>
        </Instructions>
      </DropArea>
      <Files width={width}>
        {value &&
          [...value, ...uploads].map(upload => {
            let p;
            if (upload.id) {
              p = 100;
            } else if (upload.progress) {
              p = Math.round(
                (upload.progress.loaded * 100) / upload.progress.total
              );
            } else {
              p = 0;
            }

            return (
              <File
                key={getName(upload)}
                p={p}
                handleRemove={handleRemove}
                upload={upload}
              />
            );
          })}
        {erroredUploads.map(upload => (
          <File
            key={getName(upload)}
            p={100}
            handleRemove={handleRemove}
            upload={upload}
            error
          />
        ))}
      </Files>
    </div>
  );
};

const Header = styled.h4`
  // color: ${blue};
`;

const DropArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  color: ${props => (props.reject ? red : blue)};
  border: 2px dashed ${props => (props.reject ? red : blue)};
  // margin-top: 20px;
  border-radius: 10px;
  cursor: pointer;
  background-color: white;

  ${props => props.active && !props.reject && `background-color: #eef7fb;`}
  width: ${props => props.width || '300px'};

  margin-bottom: 32px;
`;

const Instructions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const InstructionText = styled.div`
  text-align: center;
`;

const UploadIcon = styled.img`
  height: 32px;
  margin-bottom: 16px;
`;

const Files = styled.div`
  width: ${props => props.width || '300px'};
`;

export default Dropzone;
